/**
 * What a status means, and therefore what colour it wears.
 *
 * Five tones, not a colour per feature: a run, a test, a connection and a
 * setup step all say the same few things, and the reader should learn the
 * colour once. The colours themselves live in one `@theme` block in
 * globals.css (`--color-success`, `--color-danger`, `--color-attention`,
 * `--color-busy`, `--color-idle`) — change them there and every status in
 * the app follows.
 */
export type StatusTone = "success" | "danger" | "attention" | "busy" | "idle";

/** Plain coloured text: a word at the end of a line, an inline label. */
export const toneText: Record<StatusTone, string> = {
  success: "text-success",
  danger: "text-danger",
  attention: "text-attention",
  busy: "text-busy",
  idle: "text-idle",
};

/** A pill: tinted fill, own border, readable text. */
export const toneBadge: Record<StatusTone, string> = {
  success: "bg-success/12 text-success border-success/25",
  danger: "bg-danger/12 text-danger border-danger/25",
  attention: "bg-attention/12 text-attention border-attention/25",
  busy: "bg-busy/12 text-busy border-busy/25",
  idle: "bg-idle/10 text-idle border-idle/20",
};

/** A small filled circle, for lists too dense for a pill. */
export const toneDot: Record<StatusTone, string> = {
  success: "bg-success",
  danger: "bg-danger",
  attention: "bg-attention",
  busy: "bg-busy",
  idle: "bg-idle",
};

/**
 * The words the app already uses for state, in every spelling they appear
 * in: run rows, test results, connection rows, triggers, alerts, requests.
 * Unknown words are idle rather than alarming — a status nobody mapped is
 * not a failure.
 */
const TONE_BY_WORD: Record<string, StatusTone> = {
  // Finished well.
  success: "success",
  succeeded: "success",
  passed: "success",
  ready: "success",
  active: "success",
  connected: "success",
  enabled: "success",
  approved: "success",
  resolved: "success",
  complete: "success",
  completed: "success",
  done: "success",
  ok: "success",
  healthy: "success",
  // Went wrong.
  error: "danger",
  errored: "danger",
  failed: "danger",
  failure: "danger",
  crash: "danger",
  crashed: "danger",
  blocked: "danger",
  disconnected: "danger",
  expired: "danger",
  exceeded: "danger",
  // Yellow means a person needs to act; ordinary waiting stays neutral.
  warning: "attention",
  warn: "attention",
  attention: "attention",
  opportunity: "attention",
  opportunities: "attention",
  // Generic lifecycle words do not establish a need for action. Request and
  // setup panels classify human decisions explicitly; deliberate pauses stay neutral.
  pending: "idle",
  open: "idle",
  waiting: "idle",
  review: "idle",
  draft: "idle",
  unconfigured: "idle",
  disabled: "idle",
  paused: "idle",
  // Happening right now.
  running: "busy",
  queued: "busy",
  starting: "busy",
  generating: "busy",
  testing: "busy",
  info: "busy",
  // Nothing has happened yet.
  idle: "idle",
  none: "idle",
  never: "idle",
  unknown: "idle",
  dismissed: "idle",
  skipped: "idle",
  planned: "idle",
};

/** The tone for a status word, however the surface spells it. */
export function toneFor(status: string | null | undefined): StatusTone {
  if (!status) return "idle";
  const word = status.trim().toLowerCase();
  return TONE_BY_WORD[word] ?? TONE_BY_WORD[word.split(/[\s_-]+/)[0]] ?? "idle";
}

/** The tone for a plain yes/no state: tested or not, connected or not. */
export function toneForDone(done: boolean, undone: StatusTone = "idle"): StatusTone {
  return done ? "success" : undone;
}
